import { Button, Menu, Skeleton, UnstyledButton } from '@mantine/core';
import { IconBuildings, IconCheck, IconChevronDown, IconFolder } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useMatchRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { Fragment, useEffect } from 'react';

import { api } from '@/clients/admin.client';
import { AllowedProject, Tenant } from '@/clients/types';
import { LS_KEY_CURRENT_PROJECT } from '@/const';
import { isAdmin, isPersonalTenant, UserInfo } from '@/lib/auth';
import { logger } from '@/lib/logger';

type Props = {
  userInfo: UserInfo;
  adminClient: typeof api;
  projectId?: string | null;
};

export function ProjectSelector({ userInfo, adminClient, projectId }: Props) {
  const projectsQuery = useQuery({
    queryKey: ['projects'],
    queryFn: () => adminClient.settings_v1_get_projects(),
  });
  const tenantQuery = useQuery({
    queryKey: ['tenant'],
    queryFn: () => adminClient.settings_v1_get_tenant(),
  });

  // 個人テナントには選ぶものが無いので、読み込み中とエラーの枠も出さない
  const hasChoice = !isPersonalTenant(userInfo);
  if (projectsQuery.isLoading || tenantQuery.isLoading) {
    return hasChoice ? <Skeleton h={32} w={160} radius="xl" /> : null;
  }
  if (projectsQuery.isError || tenantQuery.isError) {
    return hasChoice ? (
      <Button variant="subtle" color="gray" radius="xl" h={40} disabled>
        読み込めませんでした
      </Button>
    ) : null;
  }
  return (
    <ProjectSelect
      userInfo={userInfo}
      tenant={tenantQuery.data!}
      projects={projectsQuery.data?.items ?? []}
      originalProject={projectId}
    />
  );
}

type ProjectSelectProps = {
  userInfo: UserInfo;
  tenant: Tenant;
  projects: AllowedProject[];
  originalProject?: string | null;
};

function ProjectSelect({ userInfo, tenant, projects, originalProject }: ProjectSelectProps) {
  const tenantValue = '__tenant__';
  const navigate = useNavigate();
  const matchRoute = useMatchRoute();
  const isProjectRoute = !!matchRoute({ to: '/project', fuzzy: true });
  const isTenantRoute = !!matchRoute({ to: '/tenant', fuzzy: true });

  // ヘッダーとナビに 1 つずつ置く（幅で出し分ける）ので、選択は各コピーの state ではなく
  // URL に持たせる。state だと隠れている側が切り替えに追従せず、幅が変わったときに古い
  // プロジェクトを出す
  const { project: searchProject } = useSearch({ from: '/_main' });
  const currentProject = searchProject ?? originalProject;
  // /welcome や /upgrade（プロジェクトでもテナントでもないルート）では空にする。表示中の
  // 項目を選び直しても Select は onChange を呼ばないので、埋めるとそこへ戻れなくなる
  const selectedProject = isTenantRoute
    ? tenantValue
    : isProjectRoute
      ? projects.find((p) => p.id === currentProject)?.id ?? null
      : null;

  useEffect(() => {
    logger.debug('ProjectSelector: useEffect', {
      isProjectRoute,
      isTenantRoute,
      selectedProject,
      defaultProject: originalProject,
    });
    if (!isProjectRoute || selectedProject) {
      return;
    }
    // The URL or stored project may belong to another account used in this browser.
    if (projects.length > 0) {
      navigate({ to: '.', search: { project: projects[0].id }, replace: true });
      return;
    }
    localStorage.removeItem(LS_KEY_CURRENT_PROJECT);
  }, [projects, selectedProject, isProjectRoute, isTenantRoute, originalProject, navigate]);

  // A personal tenant has only its own project: the selector still resolves it
  // into the URL above, but there is nothing to choose.
  if (isPersonalTenant(userInfo)) {
    return null;
  }
  const items = projects.map((project) => ({
    value: project.id,
    label: project.name,
  }));
  const selectionData = [{ group: 'プロジェクト', items }];
  if (isAdmin(userInfo)) {
    selectionData.unshift({
      group: 'テナント',
      items: [{ value: tenantValue, label: tenant.spec.name }],
    });
  }
  const select = (value: string) => {
    if (value === tenantValue) {
      navigate({ to: '/tenant' });
      return;
    }
    if (isProjectRoute) {
      navigate({ to: '.', search: { project: value } });
    } else {
      navigate({ to: '/project', search: { project: value } });
    }
  };
  const selectedLabel =
    selectedProject === tenantValue
      ? tenant.spec.name
      : items.find((item) => item.value === selectedProject)?.label;

  return (
    <Menu position="bottom-start" shadow="md" width={260}>
      <Menu.Target>
        {/* ヘッダーはナビであってフォームではないので、入力欄ではなく現在地のチップにする */}
        <UnstyledButton className="flex h-10 max-w-[200px] items-center gap-1.5 rounded-full px-3 text-sm hover:bg-plamo-muted md:max-w-[260px]">
          {/* 建物の記号同士（組織と事業所）は 16px では見分けられないので、
              プロジェクトはフォルダにする */}
          {isTenantRoute ? (
            <IconBuildings size={16} className="shrink-0" />
          ) : (
            <IconFolder size={16} className="shrink-0" />
          )}
          <span className="truncate">{selectedLabel ?? 'プロジェクトを選択'}</span>
          <IconChevronDown size={14} className="shrink-0" />
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>
        {selectionData.map((group) => (
          <Fragment key={group.group}>
            <Menu.Label>{group.group}</Menu.Label>
            {group.items.map((item) => (
              <Menu.Item
                key={item.value}
                onClick={() => select(item.value)}
                leftSection={
                  item.value === tenantValue ? (
                    <IconBuildings size={16} />
                  ) : (
                    <IconFolder size={16} />
                  )
                }
                rightSection={item.value === selectedProject ? <IconCheck size={14} /> : null}
              >
                {item.label}
              </Menu.Item>
            ))}
          </Fragment>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}
